import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { onAuthStateChange, handleRedirectResult, signOutUser } from '../lib/auth';
import { authenticatedFetch } from '../lib/apiClient';
import { auth } from '../firebase';
import { isInternalLoginRole } from '../lib/internalLogin';

/**
 * 使用者角色類型
 */
export type UserRole = 'admin' | 'operator' | 'customer' | 'partner';

/**
 * 使用者角色資訊
 */
export interface UserRoleInfo {
  id: string;
  user_id: string;
  email: string;
  role: UserRole;
  customer_id?: string;
  partner_id?: string;
  status: 'active' | 'inactive' | 'suspended';
  skills?: string[]; // 技能列表（僅 operator 和 admin）
  created_at: string;
  updated_at: string;
}

/**
 * 認證上下文類型
 */
interface AuthContextType {
  // Firebase 使用者
  user: User | null;
  
  // 使用者角色資訊
  userRole: UserRoleInfo | null;
  
  // 載入狀態
  loading: boolean;
  authInitialized: boolean;
  roleLoadError: string | null;
  
  // 角色檢查輔助函數
  isAdmin: () => boolean;
  isOperator: () => boolean;
  isCustomer: () => boolean;
  isPartner: () => boolean;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  
  // 專案權限檢查
  hasProjectPermission: (projectId: string, permissionType?: string) => Promise<boolean>;
  
  // 登出函數
  signOut: () => Promise<void>;
  
  // 重新載入使用者角色
  refreshUserRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * 從後端 API 獲取使用者角色資訊
 */
const fetchUserRole = async (): Promise<UserRoleInfo | null> => {
  try {
    // 如果沒有用戶，直接返回 null，不要發送請求
    if (!auth.currentUser) {
      return null;
    }

    const response = await authenticatedFetch('/api/users/me');

    if (!response.ok) {
      // 嘗試獲取錯誤訊息
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: `HTTP ${response.status} ${response.statusText}` };
      }
      // eslint-disable-next-line no-console
      console.error('❌ fetchUserRole: API 請求失敗', {
        status: response.status,
        error: errorData
      });
      throw new Error(`角色查詢失敗 (${response.status})`);
    }
    
    // 直接解析 JSON，如果失敗則返回 null
    let data;
    try {
      data = await response.json();
    } catch (jsonError) {
      // eslint-disable-next-line no-console
      console.error('❌ fetchUserRole: 解析 JSON 失敗', jsonError);
      return null;
    }

    if (!data.success) {
      // eslint-disable-next-line no-console
      console.error('❌ fetchUserRole: API 回應 success 為 false', data);
      return null;
    }
    
    // 檢查是否有角色資訊
    if (!data.data) {
      return null;
    }

    // 如果使用者沒有角色（status === 'no_role' 或沒有 role）
    if (data.data.status === 'no_role' || !data.data.role || !isInternalLoginRole(data.data.role)) {
      return null;
    }

    return data.data as UserRoleInfo;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('❌ fetchUserRole: 獲取使用者角色時發生錯誤', {
      error,
      message: error instanceof Error ? error.message : '未知錯誤',
      stack: error instanceof Error ? error.stack : undefined
    });
    throw error;
  }
};

/**
 * AuthProvider 組件
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRoleInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [roleLoadError, setRoleLoadError] = useState<string | null>(null);

  // 初始化認證狀態。只訂閱一次；authInitialized 若放進依賴，第一次空狀態會拆掉監聽，登入結果就收不到。
  useEffect(() => {
    let isMounted = true;
    let generation = 0;
    let resolvingRole = false;

    const handleAuthStateChange = async (firebaseUser: User | null) => {
      if (!isMounted) {
        return;
      }

      // 較早的空狀態回呼可能晚於已完成的登入，不能把已登入的使用者蓋掉。
      if (!firebaseUser && auth.currentUser) {
        return;
      }

      const currentGeneration = ++generation;
      setUser(firebaseUser);

      if (firebaseUser) {
        resolvingRole = true;
        setLoading(true);
        setRoleLoadError(null);

        // 慢回應先結束全頁載入，但不能把尚未回來的角色改成「沒有角色」。
        const slowTimer = window.setTimeout(() => {
          if (isMounted && currentGeneration === generation) {
            setAuthInitialized(true);
            setLoading(false);
          }
        }, 20000);

        try {
          const roleInfo = await fetchUserRole();
          if (!isMounted || currentGeneration !== generation) {
            return;
          }
          if (auth.currentUser?.uid !== firebaseUser.uid) {
            return;
          }
          setUserRole(roleInfo);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('❌ 獲取使用者角色時發生錯誤:', error);
          if (!isMounted || currentGeneration !== generation) {
            return;
          }
          setUserRole(null);
          setRoleLoadError('無法連線到系統，請確認本機後端已啟動後再試一次');
        } finally {
          window.clearTimeout(slowTimer);
          if (isMounted && currentGeneration === generation) {
            resolvingRole = false;
            setAuthInitialized(true);
            setLoading(false);
          }
        }
        return;
      }

      resolvingRole = false;
      setUserRole(null);
      setRoleLoadError(null);
      setAuthInitialized(true);
      setLoading(false);
    };

    const unsubscribe = onAuthStateChange(handleAuthStateChange);

    handleRedirectResult().catch(() => {
      // 重定向結果失敗時仍由 auth state listener 決定畫面
    });

    const timeoutId = setTimeout(() => {
      if (isMounted && !resolvingRole && !auth.currentUser) {
        setAuthInitialized(true);
        setLoading(false);
      }
    }, 500);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  // 角色檢查輔助函數
  const isAdmin = (): boolean => {
    return userRole?.role === 'admin';
  };

  const isOperator = (): boolean => {
    return userRole?.role === 'operator';
  };

  const isCustomer = (): boolean => {
    return userRole?.role === 'customer';
  };

  const isPartner = (): boolean => {
    return userRole?.role === 'partner';
  };

  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!userRole) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(userRole.role);
  };

  // 專案權限檢查
  const hasProjectPermission = async (projectId: string, permissionType: string = 'view'): Promise<boolean> => {
    if (!user || !userRole) return false;
    
    // Admin 和 Operator 有所有權限
    if (isAdmin() || isOperator()) return true;
    
    try {
      const response = await authenticatedFetch(
        `/api/project-permissions/me?projectId=${projectId}&permissionType=${permissionType}`
      );
      const data = await response.json();
      
      return data.success && data.data?.has_permission === true;
    } catch {
      return false;
    }
  };

  // 登出函數
  const signOut = async (): Promise<void> => {
    await signOutUser();
    setUser(null);
    setUserRole(null);
  };

  // 重新載入使用者角色
  const refreshUserRole = async (): Promise<void> => {
    if (!user) return;
    
    const roleInfo = await fetchUserRole();
    setUserRole(roleInfo);
  };

  const value: AuthContextType = {
    user,
    userRole,
    loading,
    authInitialized,
    roleLoadError,
    isAdmin,
    isOperator,
    isCustomer,
    isPartner,
    hasRole,
    hasProjectPermission,
    signOut,
    refreshUserRole
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth Hook - 使用認證上下文
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth 必須在 AuthProvider 內部使用');
  }
  return context;
};

export default AuthContext;

